import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AccountFilterComponent } from '../../../../_shared/components';
import { isEmptyObject } from '../../../../_shared/helpers/object.helper';
import { AccountFilterData, DriverAssignmentFilter } from '../../../../_shared/models';
import { VehicleAssignmentListService } from '../../services/vehicle-assignment-list.service';
import { AssignedVehicleDetails, AssignedVehiclesPaginatedRequest, ModalConfig } from '../../models/vehicle-assignment.model';
import { ErrorMessages } from '../../../../_shared/constants';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { TruncatetextPipe } from '../../../../_shared/pipes/truncatetext.pipe';
import { NgbModal, NgbModalOptions, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SearchComponent } from '../../../../_shared/components/filters/search/search.component';
import { PaginationComponent } from "../../../../_shared/components/pagination/pagination.component";
import { AssignmentFilterService } from '../../services/assignment-filter.service';
import { BulkAssignmentModalConfig, BulkUnAssignmentModalConfig, SingleAssignmentModalConfig, SingleUnAssignmentModalConfig } from '../../../../_shared/constants/assignment-modal-config.constants';
import { VehicleAssignmentModalComponent } from '../../../_shared/components/modals/driver-assignment/vehicle-assignment-modal.component';
import { AssignmentFilterType } from '../../../../_shared/enums/assignment-filter-type.enum';
import { AssignmentType } from '../../../../_shared/enums/assignment-type.enum';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { CustomDropdownComponent } from "../../../../_shared/components/custom-dropdown/custom-dropdown.component";
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-vehicle-assignment-list',
  standalone: true,
  imports: [AccountFilterComponent, RouterModule, CommonModule, TruncatetextPipe, NgbTooltipModule, SearchComponent, PaginationComponent, NgbDropdownModule, CustomDropdownComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './driver-vehicle-assignment-list.component.html',
  styleUrl: './driver-vehicle-assignment-list.component.scss'
})
export class DriverVehicleAssignmentListComponent implements OnInit {
  driverId: number;
  fullName: string = '';
  hasFleetManagerRole: boolean;
  isDriverDeleted: boolean;
  accountFilter: AccountFilterData;
  isLoading: boolean = true;
  hasLoaded: boolean = false;
  assignedVehicles: AssignedVehicleDetails[] = [];
  allAssignedVehicles: AssignedVehicleDetails[] = [];
  selectedVehicleIds: Set<number> = new Set();
  assignmentFilterType = AssignmentFilterType;
  accountFilterChangedSubject: Subject<number[]> = new Subject<number[]>();
  accountFilterChanged$: Observable<number[]> = this.accountFilterChangedSubject.asObservable();
  totalItems: number = 0;
  currentItems: number = 0;
  numberOfPages: number = 0;
  currentPage: number = 1;
  paginatedRequest: AssignedVehiclesPaginatedRequest = {
    accountIds: [],
    page: 1,
    pageSize: 10,
    searchTerm: ''
  };
  private cancelSubject: Subject<void> = new Subject<void>();
  private readonly transloco = inject(TranslocoService);
  private readonly assignmentFilterOptions: { type: AssignmentFilterType; key: string }[] = [
    { type: AssignmentFilterType.Assigned, key: 'vehicleAssignment.filters.assigned' },
    { type: AssignmentFilterType.PendingAssignment, key: 'vehicleAssignment.filters.pendingAssignment' },
    { type: AssignmentFilterType.PendingUnassignment, key: 'vehicleAssignment.filters.pendingUnassignment' },
  ];
  assignmentOptions: string[] = this.assignmentFilterOptions.map((option) => this.transloco.translate(option.key));
  selectedAssignment: string | null = null;

  constructor(
    private router: Router,
    private vehicleAssignmentListService: VehicleAssignmentListService,
    private toastr: ToastrService,
    private assignmentFilterService: AssignmentFilterService,
    private modalService: NgbModal
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state;
    this.driverId = state?.['driverId'];
    this.fullName = state?.['fullName'];
    this.hasFleetManagerRole = state?.['hasFleetManagerRole']
    this.isDriverDeleted = state?.['isDriverDeleted'];
    this.accountFilter = {} as AccountFilterData;
  }

  ngOnInit(): void {
    if (!this.driverId && !this.fullName) {
      this.router.navigate(['/driver-management/driver-vehicle-assignment']);
    }
    this.handleSelectedFilter()
  }

  handleSelectedFilter() {
    const currentFilter = this.assignmentFilterService.selectedFilterType();
    const option = this.assignmentFilterOptions.find((item) => item.type === currentFilter);
    this.selectedAssignment = option ? this.transloco.translate(option.key) : null;
  }

  async onAccountFilterChange(filter: AccountFilterData) {
    if (!isEmptyObject(filter)) {
      this.accountFilter = filter;
      this.accountFilterChangedSubject.next(filter.accountIds);
      this.paginatedRequest.accountIds = filter.accountIds;
      await this.getAssignedVehicles();
    }
  }

  async onSearchTermUpdate(searchTerm: string) {
    this.paginatedRequest.searchTerm = searchTerm;
    this.paginatedRequest.page = 1;
    await this.getAssignedVehicles();
  }

  selectAssignment(option: string): void {
    this.selectedAssignment = option;

    const selected = this.assignmentFilterOptions.find((item) => this.transloco.translate(item.key) === option);
    if (selected) {
      this.assignmentFilterService.selectedFilterType.set(selected.type);
    }

    this.getAssignedVehicles();
  }

  async createPagination(page: number) {
    if (page > this.numberOfPages || page < 1) {
      return;
    }

    this.paginatedRequest.page = page;
    this.currentPage = page;

    await this.getAssignedVehicles();
  }

  async getAssignedVehicles(): Promise<void> {
    try {
      this.cancelSubject.next();
      this.isLoading = true;

      this.paginatedRequest.driverId = this.driverId;
      this.paginatedRequest.isSynced = this.assignmentFilterService.selectedFilterType() === AssignmentFilterType.Assigned;
      this.paginatedRequest.assignmentTypeId = this.getAssignmentTypeId();

      const response = await this.vehicleAssignmentListService.getDriverVehicles(this.paginatedRequest, this.cancelSubject);
      const mergedAssignedVehicles = [
        ...this.allAssignedVehicles,
        ...(response.data?.results ?? []),
      ];

      this.allAssignedVehicles = mergedAssignedVehicles.filter((a, index, self) =>
        index === self.findIndex(asset => asset.assetId === a.assetId)
      );

      this.assignedVehicles = response.data?.results ?? [];
      this.totalItems = response.data?.totalCount ?? 0;
      this.currentItems = Math.min(this.currentPage * this.paginatedRequest.pageSize, this.totalItems);
      this.numberOfPages = response.data?.totalPages ?? 0;
      this.hasLoaded = true;

    } catch (error: any) {
      if (error.name === 'EmptyError') {
        // NOTE: Swallow this error. This is caused by cancelling the request
        return;
      }
      const message = ErrorMessages[error?.error?.errorCode] || ErrorMessages['errors/default'];
      this.toastr.error(message);
    } finally {
      this.isLoading = false;
    }
  }

  getAssignmentTypeId(): AssignmentType {
    if (this.assignmentFilterService.selectedFilterType() === AssignmentFilterType.PendingAssignment) {
      return AssignmentType.Assign;
    }

    if (this.assignmentFilterService.selectedFilterType() === AssignmentFilterType.PendingUnassignment) {
      return AssignmentType.Unassign;
    }

    return AssignmentType.Assign;
  }


  getSelectionState(): string {
    if (this.selectedVehicleIds.size === 0 && this.totalItems === 0) {
      return 'none';
    }

    if (this.selectedVehicleIds.size === this.totalItems) {
      return 'all';
    }

    if (this.selectedVehicleIds.size > 0) {
      return 'partial';
    }

    return 'none';
  }

  onRowSelectionChange(vehicleId: number, event: Event): void {
    const target = event?.target as HTMLInputElement;
    const checked = target.checked;

    if (checked) {
      this.selectedVehicleIds.add(vehicleId);
    } else {
      this.selectedVehicleIds.delete(vehicleId);
    }
  }

  toggleAllSelection(): void {
    const isAllChecked = this.isAllSelected();
    const currentPageAssetIds = this.assignedVehicles.map(vehicle => vehicle.assetId);

    if (!isAllChecked) {
      currentPageAssetIds.forEach(assetId => this.selectedVehicleIds.add(assetId));
      return;
    }

    if (isAllChecked) {
      currentPageAssetIds.forEach(assetId => this.selectedVehicleIds.delete(assetId));
      return;
    }
  }

  isAllSelected(): boolean {
    return this.assignedVehicles.every(vehicle => this.selectedVehicleIds.has(vehicle.assetId));
  }

  public getNumbers(): number[] {
    return this.numberOfPages > 0 ? Array.from({ length: this.numberOfPages }, (_, i) => i + 1) : [];
  }

  isActive(filterType: AssignmentFilterType): boolean {
    return this.assignmentFilterService.selectedFilterType() === filterType;
  }

  handleSingleUnassignmentModal(vehicle: any): void {
    const config = SingleUnAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.unassignDriver.title'),
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.unassignDriver.message', { driver: this.fullName, vehicle: vehicle.assetName }),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.unassignDriver.warning'),
      assignmentFilter: {
        drivers: [{
          id: this.driverId
        }],
        assets: [{
          id: vehicle.assetId,
          name: vehicle.assetName
        }]
      } as DriverAssignmentFilter
    };

    this.openVehicleAssignmentModal(modalConfig);
  }

  cancelPendingSingleAssignmentModal(vehicle: any) {
    const config = SingleUnAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.cancelPendingAssignment.title'),
      cancelButtonText: this.transloco.translate('vehicleAssignment.modals.noDontCancel'),
      confirmationButtonText: this.transloco.translate('vehicleAssignment.modals.yesCancel'),
      confirmPlaceholder: this.transloco.translate('vehicleAssignment.modals.typeCancelToConfirm'),
      confirmationPrompt: this.transloco.translate('vehicleAssignment.modals.cancelPrompt'),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingAssignment.warning'),
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingAssignment.message', { driver: this.fullName, vehicle: vehicle.assetName }),
      assignmentFilter: {
        drivers: [{
          id: this.driverId
        }],
        assets: [{
          id: vehicle.assetId,
          name: vehicle.assetName
        }]
      } as DriverAssignmentFilter
    };

    this.openVehicleAssignmentModal(modalConfig)
  }

  cancelPendingSingleUnassignmentModal(vehicle: any) {
    const config = SingleAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      showAssetFilter: false,
      showDrivers: false,
      showAssets: false,
      showConfirmationMessage: true,
      showConfirmationWarning: true,
      title: this.transloco.translate('vehicleAssignment.modals.cancelPendingUnassignment.title'),
      confirmationButtonText: this.transloco.translate('vehicleAssignment.modals.yesCancel'),
      confirmPlaceholder: this.transloco.translate('vehicleAssignment.modals.typeCancelToConfirm'),
      confirmationPrompt: this.transloco.translate('vehicleAssignment.modals.cancelPrompt'),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingUnassignment.warning'),
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingUnassignment.message', { driver: this.fullName, vehicle: vehicle.assetName }),
      assignmentFilter: {
        drivers: [{
          id: this.driverId
        }],
        assets: [{
          id: vehicle.assetId,
          name: vehicle.assetName
        }]
      } as DriverAssignmentFilter
    }

    this.openVehicleAssignmentModal(modalConfig);
  }

  handleBulkUnassignmentModal(): void {
    const config = BulkUnAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.bulkUnassignDriver.title'),
      showConfirmationMessage: true,
      showConfirmationWarning: true,
      showAssetFilter: false,
      showDrivers: false,
      showAssets: true,
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.bulkUnassignDriver.message', { driver: this.fullName }),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.bulkUnassignDriver.warning'),
      assignmentFilter: {
        drivers: [{
          id: this.driverId,
          name: this.fullName,
        }],
        assets: this.allAssignedVehicles.filter(vehicle => this.selectedVehicleIds.has(vehicle.assetId)).map(vehicle => ({
          id: vehicle.assetId,
          name: vehicle.assetName,
          accountName: vehicle.accountName
        }))
      } as DriverAssignmentFilter
    };
    this.openBulkAssignmentsModal(modalConfig);
  }

  cancelBulkAssignmentModal(): void {
    const config = BulkUnAssignmentModalConfig;
    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.bulkCancelAssignment.title'),
      showAssetFilter: false,
      showDrivers: false,
      showAssets: true,
      showConfirmationMessage: true,
      showConfirmationWarning: true,
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.bulkCancelAssignment.message'),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.bulkCancelAssignment.warning'),
      cancelButtonText: this.transloco.translate('vehicleAssignment.modals.noDontCancel'),
      confirmationButtonText: this.transloco.translate('vehicleAssignment.modals.yesCancel'),
      confirmPlaceholder: this.transloco.translate('vehicleAssignment.modals.typeCancelToConfirm'),
      confirmationPrompt: this.transloco.translate('vehicleAssignment.modals.cancelPrompt'),
      assignmentFilter: {
        drivers: [{
          id: this.driverId,
          name: this.fullName
        }],
        assets: this.allAssignedVehicles.filter(vehicle => this.selectedVehicleIds.has(vehicle.assetId)).map(vehicle => ({
          id: vehicle.assetId,
          name: vehicle.assetName,
          accountName: vehicle.accountName
        }))
      } as DriverAssignmentFilter
    }
    this.openBulkAssignmentsModal(modalConfig);
  }

  cancelBulkUnassignmentModal(): void {
    const config = BulkAssignmentModalConfig;
    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.bulkCancelUnassignment.title'),
      showAssetFilter: false,
      showDrivers: false,
      showAssets: true,
      showConfirmationMessage: true,
      showConfirmationWarning: true,
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.bulkCancelUnassignment.message'),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.bulkCancelUnassignment.warning'),
      cancelButtonText: this.transloco.translate('vehicleAssignment.modals.noDontCancel'),
      confirmationButtonText: this.transloco.translate('vehicleAssignment.modals.yesCancel'),
      confirmPlaceholder: this.transloco.translate('vehicleAssignment.modals.typeCancelToConfirm'),
      confirmationPrompt: this.transloco.translate('vehicleAssignment.modals.cancelPrompt'),
      assignmentFilter: {
        drivers: [{
          id: this.driverId,
          name: this.fullName
        }],
        assets: this.allAssignedVehicles.filter(vehicle => this.selectedVehicleIds.has(vehicle.assetId)).map(vehicle => ({
          id: vehicle.assetId,
          name: vehicle.assetName,
          accountName: vehicle.accountName
        }))
      } as DriverAssignmentFilter
    }
    this.openBulkAssignmentsModal(modalConfig);
  }

  private openBulkAssignmentsModal(config: ModalConfig): void {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'xl' };
    const modalRef = this.modalService.open(VehicleAssignmentModalComponent, modalOptions);
    modalRef.componentInstance.config = config;

    modalRef.closed.subscribe(() => {
      this.selectedVehicleIds.clear();
      this.paginatedRequest.page = 1;
      this.currentPage = 1;
      this.getAssignedVehicles();
    });
  }

  private openVehicleAssignmentModal(config: ModalConfig): void {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'xl' };
    const modalRef = this.modalService.open(VehicleAssignmentModalComponent, modalOptions);

    modalRef.componentInstance.config = config;

    modalRef.closed.subscribe(() => {
      this.getAssignedVehicles();
    });
  }
}